import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Send, CheckCircle2, Code, Cloud, 
  Lightbulb, Headphones, MessageSquare, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const helpItems = [
  { icon: Code, text: 'Custom software & application development' },
  { icon: Cloud, text: 'SaaS platform builds and scaling' },
  { icon: Cloud, text: 'Cloud infrastructure, migrations, and optimization' },
  { icon: Lightbulb, text: 'IT consulting & technical strategy' },
  { icon: Headphones, text: 'Ongoing support and managed services' },
];

const processSteps = [
  {
    step: '1',
    title: 'Submit the Form',
    description: 'Share your project details and goals with us.',
  },
  {
    step: '2',
    title: 'We Review Your Needs',
    description: 'Our team evaluates your requirements and prepares recommendations.',
  },
  {
    step: '3',
    title: 'Expert Consultation',
    description: 'A senior team member reaches out to discuss next steps.',
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ first_name: '', last_name: '', email: '', company: '', message: '' });
      } else {
        setSubmitStatus('error');
        console.error('Contact form error:', data.detail);
      }
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Contact Las Vegas IT Services - Let's Build Something That Works"
        description="Contact Fearless IT in Las Vegas. Whether you're starting a new SaaS project, need IT consulting, or seeking technical guidance for your Nevada business, we're here to help."
        keywords="contact Las Vegas IT, Las Vegas software development inquiry, Nevada IT consulting contact, Las Vegas project consultation, Las Vegas technology partner, Henderson IT services contact"
        url="/contact"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/47ab508ad4bd244ac0faa6770d1e42f1259e830cc458f49b6376d39e9c81f9db.png"
        isLocalPage={true}
      />
      
      {/* Navigation */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-4 sm:mb-6">
            Let's Build Something That Works
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Whether you're starting a new project, need consulting, or seeking technical guidance, we're here to help you move forward with confidence.
          </p>
          <a href="#contact-form">
            <Button 
              size="lg" 
              className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all"
            >
              Start the Conversation
              <MessageSquare className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-6">
              How We Can Help
            </h2>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Fearless IT helps businesses build, scale, and optimize technology. Share your goals with us, and a senior engineer or solutions architect will personally review your inquiry and respond with clear, actionable guidance.
            </p>
          </div>

          {/* What We Can Help With */}
          <div className="grid sm:grid-cols-2 gap-4">
            {helpItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div 
                  key={index}
                  className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                >
                  <div className="w-10 h-10 rounded-lg gradient-fearless flex items-center justify-center flex-shrink-0">
                    <IconComponent className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
              What Happens Next
            </h2>
            <p className="text-gray-600">
              No pressure, no sales scripts. Just expert guidance.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full gradient-fearless text-white font-bold text-xl mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-16 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-4">
              Get in Touch
            </h2>
            <p className="text-gray-600">
              Tell us about your project and we'll get back to you within one business day.
            </p>
          </div>

          {submitStatus === 'success' ? (
            <div className="text-center py-12 px-6 bg-green-50 rounded-2xl border border-green-100">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Message Sent!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for reaching out. A senior team member will review your inquiry and get back to you within one business day.
              </p>
              <Button 
                onClick={() => setSubmitStatus(null)}
                variant="outline"
                className="border-gray-300"
              >
                Send Another Message
              </Button>
            </div>
          ) : submitStatus === 'error' ? (
            <div className="text-center py-12 px-6 bg-red-50 rounded-2xl border border-red-100">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
                <Send className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-black mb-3">Something Went Wrong</h3>
              <p className="text-gray-600 mb-6">
                We couldn't send your message. Please try again or email us directly.
              </p>
              <Button 
                onClick={() => setSubmitStatus(null)}
                variant="outline"
                className="border-gray-300"
              >
                Try Again
              </Button>
            </div>
          ) : (
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500 rounded-2xl opacity-30 group-hover:opacity-50 blur transition-all duration-500"></div>
              <form 
                onSubmit={handleSubmit}
                className="relative bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-10"
              >
                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-gray-700 font-medium">
                      First Name *
                    </Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={handleInputChange}
                      placeholder="John"
                      className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-gray-700 font-medium">
                      Last Name *
                    </Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={handleInputChange}
                      placeholder="Smith"
                      className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@company.com"
                      className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-700 font-medium">
                      Company Name
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Your Company"
                      className="h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-2 mb-8">
                  <Label htmlFor="message" className="text-gray-700 font-medium">
                    Project Description *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your project, goals, and any specific challenges you're facing..."
                    className="min-h-[150px] border-gray-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full gradient-fearless text-white py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-20 sm:py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-4">
            Build Confident.
          </h2>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient mb-6">
            Scale Fearlessly.
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Partner with a team that values clarity, reliability, and results. We're here to help you build technology that works and lasts.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
};

export default ContactPage;
